import { ChangeDetectorRef, Component, Input } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import type { Observable, Subject } from 'rxjs';
import { merge, takeUntil } from 'rxjs';

import type { FormGroupOf } from '@core/common-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import { FixedSelectOptionsHelper } from '@core/offers-logic';
import type { SalaryData } from '@core/offers-types';
import type { SelectOption } from '@core/user-interface-legacy';
import {
  FormFieldComponent,
  IconHelpComponent,
  LabelComponent,
  NumericInputComponent,
  ProtectedControlDirective,
  RtTooltip,
  SelectComponent,
} from '@core/user-interface-legacy';
import { LifeCycle } from '@core/utils-legacy';
import { CurrencySelectFieldComponent } from '@recruitee/currency-ui';

@Component({
  selector: 'rt-salary-form-group',
  templateUrl: './salary-form-group.component.html',
  styleUrls: ['./salary-form-group.component.less'],
  imports: [
    CurrencySelectFieldComponent,
    FormFieldComponent,
    FormsModule,
    IconHelpComponent,
    LabelComponent,
    ProtectedControlDirective,
    RtTooltip,
    SelectComponent,
    TranslatePipe,
    ReactiveFormsModule,
    NumericInputComponent,
  ],
})
export class SalaryFormGroupComponent {
  @Input() public salaryFormGroup: FormGroupOf<SalaryData>;
  @Input() public isDisabled: boolean = false;
  @Input() public isReadOnly: boolean = false;
  @Input() public hasRequiredAsterisk: boolean = false;
  @Input() public updateFormattedValueDispatcher?: Observable<void>;
  @Input() public showErrorMessages: boolean = false;

  public salaryPeriodSelectOptions: SelectOption[] =
    this.fixedSelectOptions.getSalaryPeriodSelectOptions();

  @LifeCycle('onDestroy')
  private ngOnDestroy$: Subject<void>;

  constructor(
    private cdRef: ChangeDetectorRef,
    private fixedSelectOptions: FixedSelectOptionsHelper,
  ) {}

  public get showNotNumberError(): boolean {
    return (
      this.salaryFormGroup.controls.min.errors?.notNumber ||
      this.salaryFormGroup.controls.max.errors?.notNumber
    );
  }

  public get showNotGraterThanOrEqualZeroError(): boolean {
    return (
      this.salaryFormGroup.controls.min.errors?.notGreaterThanOrEqualToZero ||
      this.salaryFormGroup.controls.max.errors?.notGreaterThanOrEqualToZero
    );
  }

  public get showRangeError(): boolean {
    return (
      this.salaryFormGroup.controls.min.errors?.range ||
      this.salaryFormGroup.controls.max.errors?.range
    );
  }

  public ngOnInit() {
    if (this.salaryFormGroup) {
      const minControl = this.salaryFormGroup.controls.min;
      const maxControl = this.salaryFormGroup.controls.max;

      merge(minControl.valueChanges, maxControl.valueChanges)
        .pipe(takeUntil(this.ngOnDestroy$))
        .subscribe(() => {
          minControl.updateValueAndValidity({ emitEvent: false });
          maxControl.updateValueAndValidity({ emitEvent: false });
          this.cdRef.detectChanges();
        });
    }
  }
}
